import React, { useState, useEffect } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { SlidersHorizontal, Save, RotateCcw, TrendingDown, TrendingUp } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA' };

export default function ScoreBandsPage() {
  const api = useApi();
  const { user } = useAuth();
  const canEdit = ['mga_admin', 'platform_owner'].includes(user?.role);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/marais/score-bands').then(r => setCfg(r.data)).catch(err => console.error('score bands:', err?.message));
  }, [api]);

  if (!cfg) return <Skeleton className="h-64 rounded-sm" />;

  const setBand = (i, k, v) => setCfg(c => ({
    ...c, bands: c.bands.map((b, idx) => idx === i ? { ...b, [k]: v } : b),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/marais/score-bands', {
        enabled: cfg.enabled,
        bands: cfg.bands.map(b => ({ ...b, max_score: Number(b.max_score), adjustment_pct: Number(b.adjustment_pct) })),
      });
      setCfg(data);
      toast.success('Score bands saved — applies to every new quote immediately');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  let prev = 0;
  return (
    <div className="max-w-3xl space-y-4" data-testid="score-bands-page">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: 'rgba(0,180,216,0.12)' }}>
          <SlidersHorizontal className="w-5 h-5" style={{ color: C.teal }} />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold" style={{ color: C.navy }}>Score-Band Premium Loading</h2>
          <p className="text-xs mt-0.5" style={{ color: C.slate }}>
            The Fraime composite score (0–10,000, lower is cleaner) automatically adjusts every quote:
            preferred risks earn a discount, elevated risks pay a loading. Shown transparently on the itemised quote.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs" style={{ color: C.slate }}>{cfg.enabled ? 'Active' : 'Off'}</span>
          <Switch checked={cfg.enabled} disabled={!canEdit} onCheckedChange={v => setCfg(c => ({ ...c, enabled: v }))} data-testid="score-bands-enabled" />
        </div>
      </div>

      <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid="score-bands-table">
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider font-semibold pb-2 border-b mb-1" style={{ color: C.slate, borderColor: 'rgba(136,150,168,0.15)' }}>
          <span className="col-span-3">Band</span>
          <span className="col-span-3">Score Range</span>
          <span className="col-span-3">Up To Score</span>
          <span className="col-span-3 text-right">Premium Adjustment</span>
        </div>
        {cfg.bands.map((b, i) => {
          const from = i === 0 ? 0 : Number(cfg.bands[i - 1].max_score) + 1;
          prev = b.max_score;
          const pct = Number(b.adjustment_pct);
          return (
            <div key={i} className="grid grid-cols-12 gap-2 items-center py-2 border-b last:border-0" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`score-band-row-${i}`}>
              <Input value={b.label} disabled={!canEdit} onChange={e => setBand(i, 'label', e.target.value)}
                className="col-span-3 rounded-sm h-8 text-xs" data-testid={`band-label-${i}`} />
              <span className="col-span-3 text-xs font-mono" style={{ color: C.slate }}>{from.toLocaleString()} – {Number(b.max_score).toLocaleString()}</span>
              <Input type="number" value={b.max_score} disabled={!canEdit || i === cfg.bands.length - 1}
                onChange={e => setBand(i, 'max_score', e.target.value)}
                className="col-span-3 rounded-sm h-8 text-xs" data-testid={`band-max-${i}`} />
              <div className="col-span-3 flex items-center justify-end gap-1.5">
                {pct < 0 ? <TrendingDown className="w-3.5 h-3.5" style={{ color: C.green }} /> : pct > 0 ? <TrendingUp className="w-3.5 h-3.5" style={{ color: C.red }} /> : null}
                <Input type="number" value={b.adjustment_pct} disabled={!canEdit}
                  onChange={e => setBand(i, 'adjustment_pct', e.target.value)}
                  className="rounded-sm h-8 text-xs w-20 text-right font-mono" data-testid={`band-pct-${i}`}
                  style={{ color: pct < 0 ? C.green : pct > 0 ? C.red : C.navy }} />
                <span className="text-xs" style={{ color: C.slate }}>%</span>
              </div>
            </div>
          );
        })}
        <p className="text-[11px] mt-3" style={{ color: C.slate }}>
          Negative % = discount, positive % = loading. Adjustments are clamped to −50% … +100% and apply to the gross premium
          (commission, GST and stamp duty rescale with it). Existing quotes are untouched — only new scoring runs are affected.
        </p>
      </Card>

      {canEdit && (
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} className="rounded-sm text-white" style={{ background: C.blue }} data-testid="score-bands-save">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save Bands'}
          </Button>
          <Button variant="outline" className="rounded-sm" data-testid="score-bands-reset"
            onClick={async () => {
              const { data } = await api.put('/marais/score-bands', { enabled: true, bands: [] });
              setCfg(data);
              toast.success('Restored default bands');
            }}>
            <RotateCcw className="w-4 h-4 mr-1.5" /> Restore Defaults
          </Button>
        </div>
      )}
    </div>
  );
}
